import { FOCUS_RING_NO_RADIUS, Icon, Pills } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { IconCircle } from '../surfaces';

import type { User } from '@/types/directory/User';
import type { ObjectiveOrDraft } from '@/types/objectives';

import ObjectiveContributors from '@/components/objectives/ObjectiveContributors';
import { DETAIL_HERO_TITLE, GLASS_SURFACE } from '@/components/objectives/objectiveStyles';
import { ObjectiveType } from '@/constants/ObjectiveType';
import i18n from '@/plugins/i18n';
import { getLocalizedText } from '@/utils/localization';
import { formatLongDate } from '@/utils/objectivesFormat';
import { isPublishedObjective } from '@/utils/objectiveStatus';

interface ObjectiveDetailHeaderProps {
    objective: ObjectiveOrDraft;
    employeesById: Map<string, User>;
    onEdit: () => void;
}

/**
 * The page's hero card: target roundel, title, period line, the people working on it, and -- for a
 * reader who may change the objective -- the edit link in a white pill. A draft says so beside its
 * title, and has a period line only once its dates are chosen.
 */
export default function ObjectiveDetailHeader({
    objective,
    employeesById,
    onEdit,
}: ObjectiveDetailHeaderProps) {
    const { t } = useTranslation();

    const isDraft = !isPublishedObjective(objective);
    const { startDate, endDate, objectivePeriod } = objective;
    // An objective filed under a cycle is read against that cycle, but the full period still shows
    // so the objective can be placed within its cycle at a glance. A draft may have no dates yet.
    let periodLabel: string | null = null;
    if (startDate && endDate) {
        const period = {
            start: formatLongDate(startDate, i18n.language),
            end: formatLongDate(endDate, i18n.language),
        };
        periodLabel = objectivePeriod
            ? t('objectives.periodWithCycle', {
                  cycle: getLocalizedText(objectivePeriod.name),
                  ...period,
              })
            : t('objectives.periodRange', period);
    }

    return (
        <div
            className={`obj-header ${GLASS_SURFACE}`}
            data-testid={'objective-detail-header'}
        >
            <div className={'obj-header-row'}>
                <div className={'flex items-center max-sm:gap-4 sm:gap-6'}>
                    <IconCircle className={'bg-obj-tint obj-detail-hero-icon'}>
                        <Icon
                            name={'cryptocompany-target'}
                            size={36}
                            colorClassName={'text-primary'}
                            strokeWidth={1.8}
                        />
                    </IconCircle>

                    <div className={'flex min-w-0 flex-col gap-1'}>
                        <div className={'flex flex-wrap items-center gap-2'}>
                            <h1 className={DETAIL_HERO_TITLE}>{objective.title}</h1>

                            {isDraft && (
                                <Pills
                                    variant={'draftLight'}
                                    size={'tinted'}
                                    dataTestId={'objective-detail-draft'}
                                >
                                    {t('common.draft')}
                                </Pills>
                            )}

                            {objective.type === ObjectiveType.TEAM && (
                                <Pills
                                    variant={'purple'}
                                    size={'sm'}
                                >
                                    {t('objectives.card.collective')}
                                </Pills>
                            )}
                        </div>

                        {/* Always neutral, whatever the due date: reading an objective whose
                            window has closed is ordinary use — consulting an archive — and a page
                            turned red on every such visit teaches nothing. Lateness is told by the
                            badge on the objective's card in the lists. */}
                        {periodLabel && (
                            <p className={'text-obj-muted text-[13px]'}>{periodLabel}</p>
                        )}

                        <ObjectiveContributors
                            objective={objective}
                            employeesById={employeesById}
                            placement={'header'}
                        />
                    </div>
                </div>

                {objective.canEdit && (
                    <div className={'flex flex-shrink-0 self-start'}>
                        <button
                            type={'button'}
                            onClick={onEdit}
                            className={`obj-header-actions ${FOCUS_RING_NO_RADIUS} text-navy flex items-center gap-1.5 rounded-[8px] bg-white/90 text-[13px] hover:opacity-70`}
                            data-testid={'objective-detail-edit'}
                        >
                            <Icon
                                name={'edit-edit'}
                                size={'sm'}
                                color={'inherit'}
                            />
                            {t('objectives.detail.edit')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
